import { useCallback, useEffect, useRef, useState } from 'react';

import type { ComboBoxItem } from 'bundles/employability-common/components/ComboBox';

type ListState = {
  selectedId?: string; // id / key of selected item
  selectedIndex?: number;
};

export default function useKeyboardNavList(
  items: Array<ComboBoxItem>,
  onClick?: (clicked: ComboBoxItem) => void,
  enable = true
) {
  const selectedElementRef = useRef<HTMLDivElement>(null);
  const [{ selectedId, selectedIndex }, setListState] = useState<ListState>({});

  const selectedItem = selectedIndex !== undefined ? items[selectedIndex] : undefined;

  const setSelected = useCallback(
    (newSelected: string) => {
      const nextSelection = items
        .map((item, i) => ({
          selectedId: item.key,
          selectedIndex: i,
        }))
        .find(({ selectedId: key }) => key === newSelected);

      if (nextSelection) {
        setListState({ ...nextSelection });
      }
    },
    [items]
  );

  const selectDefault = useCallback(() => {
    const defaultItem = items[0];

    if (defaultItem) {
      setSelected(defaultItem.key);
    }
  }, [items, setSelected]);

  const clearSelection = useCallback(() => {
    setListState({});
  }, []);

  // if disabled, clear the state
  useEffect(() => {
    if (!enable) {
      clearSelection();
    }
  }, [enable, clearSelection]);

  // implements keyboard navigation
  useEffect(() => {
    if (enable) {
      // listen for hotkeys when its open
      const hotkeys = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          clearSelection();
          return;
        }

        if (e.key === 'Enter') {
          e.preventDefault();

          if (!selectedItem) {
            selectDefault();
          } else if (typeof onClick === 'function') {
            onClick(selectedItem);
          }
        }

        if (e.key === 'ArrowUp') {
          e.preventDefault();

          if (selectedIndex !== undefined) {
            if (selectedIndex > 0) {
              selectedElementRef.current?.scrollIntoView({ block: 'end' });
              setSelected(items[selectedIndex - 1].key);
            }
          } else {
            selectDefault();
          }
        }

        if (e.key === 'ArrowDown' || e.key === 'Tab') {
          e.preventDefault();

          if (selectedIndex !== undefined) {
            if (selectedIndex < items.length - 1) {
              selectedElementRef.current?.scrollIntoView({ block: 'start' });
              setSelected(items[selectedIndex + 1].key);
            } else if (e.key === 'Tab') {
              selectDefault();
            }
          } else {
            selectDefault();
          }
        }
      };

      document.addEventListener('keydown', hotkeys);

      return () => {
        document.removeEventListener('keydown', hotkeys);
      };
    }
    return () => undefined;
  }, [
    enable,
    items,
    selectedItem,
    selectedIndex,
    selectedElementRef,
    onClick,
    setSelected,
    selectDefault,
    clearSelection,
  ]);

  return {
    selectedId,
    selectedIndex,
    selectedItem,
    setSelected,
    selectDefault,
    clearSelection,
    selectedElementRef,
  };
}
